import { CircleAlert, Eye, Inbox, Megaphone, MessageSquareReply, RefreshCw, Send } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useUi } from '@/lib/store';
import { ck, useCampaign, useCampaignReplies, useCampaigns, type Campaign, type CampaignReply } from '@/lib/campaigns';
import { ago, fullTime } from '@/lib/format';
import { Avatar } from '@/components/Avatar';
import { Chip, cn, Empty, IconButton, Num } from '@/components/ui';

type CampaignsView = { kind: 'campaigns'; campaignId?: string; inbox?: boolean };

const when = (iso: string) => (iso ? ago(iso) : '—');

function Stat({ icon, label, value, hint, tone }: { icon: React.ReactNode; label: string; value: number; hint?: string; tone?: 'ok' | 'bad' }) {
  return (
    <div className="rounded-2xl border border-line bg-raised/60 p-3.5">
      <div className="flex items-center gap-1.5 text-[11.5px] font-medium uppercase tracking-wide text-faint [&_svg]:size-3.5">{icon}{label}</div>
      <div className={cn('mt-1 text-[24px] font-semibold tabular-nums', tone === 'bad' && 'text-danger')}><Num>{value}</Num></div>
      {hint && <div className="text-[11.5px] text-muted">{hint}</div>}
    </div>
  );
}

function CampaignRow({ c, active, onClick }: { c: Campaign; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn('w-full rounded-2xl border border-transparent px-3.5 py-3 text-left transition hover:bg-line/60', active && 'border-line bg-line/80')}>
      <div className="truncate text-[13.5px] font-semibold">{c.subject || '(no subject)'}</div>
      <div className="mt-0.5 text-[11.5px] text-muted">{c.sentAt ? `Sent ${when(c.sentAt)}` : 'Not sent yet'} · {c.sentByName}</div>
      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11.5px]">
        <Chip>{c.delivered}/{c.recipients} delivered</Chip>
        <Chip>{c.openRate}% opened</Chip>
        {c.replies > 0 && <Chip active>{c.replies} repl{c.replies === 1 ? 'y' : 'ies'}</Chip>}
        {c.failed + (c.bounced ?? 0) > 0 && <Chip>{c.failed + (c.bounced ?? 0)} failed/bounced</Chip>}
      </div>
    </button>
  );
}

function ReplyRow({ r, showCampaign }: { r: CampaignReply; showCampaign?: boolean }) {
  const setView = useUi((s) => s.setView);
  return (
    <button
      onClick={() => setView({ kind: 'mail', folder: 'Inbox', search: r.fromAddress })}
      title="Open this person's mail"
      className="flex w-full items-start gap-3 rounded-2xl px-3.5 py-3 text-left transition hover:bg-line/60"
    >
      <Avatar name={r.fromName || r.fromAddress} address={r.fromAddress} size={34} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[13.5px] font-semibold">{r.fromName || r.fromAddress}</span>
          <span className="ml-auto shrink-0 text-[11.5px] text-faint" title={fullTime(r.receivedAt)}>{when(r.receivedAt)}</span>
        </div>
        <div className="truncate text-[12.5px]">{r.subject}</div>
        <div className="truncate text-[12px] text-muted">{r.snippet}</div>
        {showCampaign && <div className="mt-1 text-[11.5px] text-faint">Re: {r.campaignSubject}</div>}
      </div>
    </button>
  );
}

function Detail({ id }: { id: string }) {
  const q = useCampaign(id);
  const replies = useCampaignReplies(id);
  const c = q.data;
  if (q.isLoading) return <div className="p-8 text-[13px] text-muted">Loading campaign…</div>;
  if (!c) return <Empty icon={<CircleAlert />} title="Couldn't load this campaign" hint={q.error instanceof Error ? q.error.message : undefined} />;
  const rows = [...c.recipientList].sort((a, b) => Number(!!b.openedAt) - Number(!!a.openedAt) || a.address.localeCompare(b.address));
  return (
    <div className="h-full overflow-y-auto p-6">
      <h2 className="text-[20px] font-semibold tracking-tight">{c.subject || '(no subject)'}</h2>
      <div className="text-[12.5px] text-muted">{c.sentAt ? `Sent ${fullTime(c.sentAt)}` : 'Not sent yet'} by {c.sentByName}</div>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Stat icon={<Send />} label="Recipients" value={c.recipients} />
        <Stat icon={<Send />} label="Delivered" value={c.delivered} hint={c.recipients ? `${Math.round((100 * c.delivered) / c.recipients)}%` : undefined} />
        <Stat icon={<Eye />} label="Opened" value={c.opened} hint={`${c.openRate}% of delivered`} />
        <Stat icon={<MessageSquareReply />} label="Replies" value={c.replies} />
        <Stat icon={<CircleAlert />} label="Failed" value={c.failed} tone={c.failed ? 'bad' : undefined} />
        <Stat icon={<CircleAlert />} label="Bounced" value={c.bounced ?? 0} tone={c.bounced ? 'bad' : undefined} hint="Rejected after sending" />
      </div>
      <p className="mt-2 text-[11.5px] text-faint">Opens are counted when the email's tracking image loads, so readers who block images are missed and some mail apps preload images.</p>

      {(replies.data?.length ?? 0) > 0 && (
        <section className="mt-6">
          <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-faint">Replies</h3>
          {replies.data!.map((r) => <ReplyRow key={r.inboxId} r={r} />)}
        </section>
      )}

      <section className="mt-6">
        <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-faint">Recipients</h3>
        <div className="overflow-hidden rounded-2xl border border-line">
          <table className="w-full text-left text-[12.5px]">
            <thead className="bg-line/50 text-[11.5px] text-muted">
              <tr><th className="px-3 py-2 font-medium">Recipient</th><th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Opened</th><th className="px-3 py-2 font-medium">Replied</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deliveryId} className="border-t border-line">
                  <td className="px-3 py-2"><div className="font-medium">{r.name || r.address}</div>{r.name && <div className="text-[11.5px] text-faint">{r.address}</div>}</td>
                  <td className="px-3 py-2">
                    <span className={cn((r.status === 'Failed' || r.status === 'Bounced') && 'text-danger')}>{r.status}</span>
                    {r.error && <div className="max-w-[260px] truncate text-[11.5px] text-danger" title={r.error}>{r.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-muted">{r.openedAt ? `${when(r.openedAt)}${r.openCount > 1 ? ` · ${r.openCount}×` : ''}` : '—'}</td>
                  <td className="px-3 py-2">{r.replied ? <span className="text-ok">Yes</span> : <span className="text-faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function Campaigns({ view }: { view: CampaignsView }) {
  const setView = useUi((s) => s.setView);
  const qc = useQueryClient();
  const list = useCampaigns();
  const inboxQ = useCampaignReplies();
  const campaigns = list.data ?? [];
  const selected = view.campaignId ?? (view.inbox ? undefined : campaigns[0]?.id);
  const refresh = () => void qc.invalidateQueries({ predicate: (q) => ['campaigns', 'campaign', 'campaign-replies'].includes(String(q.queryKey[0])) });

  return (
    <div className="flex h-full min-h-0">
      <div className="flex w-[380px] shrink-0 flex-col border-r border-line">
        <div className="flex items-end justify-between px-5 pb-3 pt-1">
          <div>
            <h1 className="page-title text-[22px] font-semibold tracking-tight">Campaigns</h1>
            <div className="text-[12px] text-muted">{campaigns.length} campaigns · {inboxQ.data?.length ?? 0} replies</div>
          </div>
          <IconButton label="Refresh" onClick={refresh}><RefreshCw className={cn(list.isFetching && 'animate-spin')} /></IconButton>
        </div>
        <div className="flex gap-1.5 px-5 pb-2">
          <Chip active={!view.inbox} onClick={() => setView({ kind: 'campaigns' })}><Megaphone className="size-3.5" />All campaigns</Chip>
          <Chip active={!!view.inbox} onClick={() => setView({ kind: 'campaigns', inbox: true })}><Inbox className="size-3.5" />Campaign inbox{!!inboxQ.data?.length && ` (${inboxQ.data.length})`}</Chip>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {list.error ? (
            <div className="p-4 text-[12.5px] text-muted">{list.error instanceof Error ? list.error.message : 'Could not load campaigns.'}</div>
          ) : view.inbox ? (
            (inboxQ.data ?? []).length === 0 ? <div className="p-4 text-[12.5px] text-muted">No replies to campaigns yet.</div> : inboxQ.data!.map((r) => <ReplyRow key={r.inboxId} r={r} showCampaign />)
          ) : (
            campaigns.map((c) => <CampaignRow key={c.id} c={c} active={c.id === selected} onClick={() => setView({ kind: 'campaigns', campaignId: c.id })} />)
          )}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        {view.inbox ? (
          <Empty icon={<Inbox />} title="Campaign inbox" hint="Replies to your campaigns from every campaign, newest first. Click one to open that person's mail." />
        ) : selected ? (
          <Detail id={selected} />
        ) : (
          <Empty icon={<Megaphone />} title="No campaigns yet" hint="Campaigns sent from your CRM show here with delivery, open and reply stats." />
        )}
      </div>
    </div>
  );
}

export { ck };
